/**
 * Tableau des Équipements de Protection Individuelle
 * Affiche les recommandations EPI avec normes EN
 */
import { SubstanceFiche } from '../../services/api'
import Card from '../ui/Card'

interface EpiTableProps {
  epi: SubstanceFiche['epi']
}

interface EpiItem {
  icon: JSX.Element
  label: string
  type: string
  norm: string
  colorClass: string
}

export default function EpiTable({ epi }: EpiTableProps) {
  const epiItems: EpiItem[] = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
      label: 'Protection respiratoire',
      type: epi.protection_respiratoire?.type || 'Non documenté',
      norm: epi.protection_respiratoire?.norm || '',
      colorClass: 'text-blue-600 bg-blue-50',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"
          />
        </svg>
      ),
      label: 'Protection des mains',
      type: epi.protection_mains?.type || 'Non documenté',
      norm: epi.protection_mains?.norm || '',
      colorClass: 'text-orange-600 bg-orange-50',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      label: 'Protection des yeux',
      type: epi.protection_yeux?.type || 'Non documenté',
      norm: epi.protection_yeux?.norm || '',
      colorClass: 'text-green-600 bg-green-50',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      label: 'Protection du corps',
      type: epi.protection_corps?.type || 'Non documenté',
      norm: epi.protection_corps?.norm || '',
      colorClass: 'text-purple-600 bg-purple-50',
    },
  ]

  const isDocumented = epi.recommandations_documentees

  return (
    <Card
      title="Équipements de Protection Individuelle"
      className={isDocumented ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-300'}
    >
      {!isDocumented && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-sm text-amber-800">
            Recommandations génériques - Consulter la fiche de données de sécurité (FDS)
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {epiItems.map((item, index) => {
          const isUndocumented =
            item.type === 'Non documenté' || item.type === '' || !item.type

          return (
            <div
              key={index}
              className={`
                flex items-start gap-4 p-4 rounded-lg border
                ${isUndocumented ? 'bg-gray-50 border-gray-200' : `${item.colorClass} border-transparent`}
              `}
            >
              <div
                className={`
                  flex-shrink-0 p-2 rounded-lg
                  ${isUndocumented ? 'bg-gray-200 text-gray-400' : item.colorClass}
                `}
              >
                {item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 text-sm">{item.label}</h4>
                <p
                  className={`mt-1 ${isUndocumented ? 'text-gray-400 italic' : 'text-gray-700'}`}
                >
                  {item.type}
                </p>
                {item.norm && !isUndocumented && (
                  <p className="mt-1 text-xs font-mono text-gray-500 bg-white bg-opacity-50 inline-block px-2 py-0.5 rounded">
                    {item.norm}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Notes additionnelles */}
      {epi.notes && epi.notes.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
          <ul className="space-y-1">
            {epi.notes.map((note, index) => (
              <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                <span className="text-gray-400">•</span>
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}
